import React, { useState } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
  useMediaQuery,
  useTheme,
  Alert,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Timeline as TimelineIcon,
  People as PeopleIcon,
  EventNote as EventNoteIcon,
  Business as BusinessIcon,
  Event as EventIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  CalendarViewMonth as CalendarViewMonthIcon,
  Inventory as InventoryIcon,
  Logout as LogoutIcon,
  AccountCircle as AccountCircleIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import indraLogo from '../assets/icons/indra.svg';

const drawerWidth = 240;
const collapsedDrawerWidth = 72;

interface Props {
  children: React.ReactNode;
}

interface MenuItem {
  text: string;
  icon: React.ReactElement;
  path: string;
}

const menuItems: MenuItem[] = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
  { text: 'Calendario Mensual', icon: <TimelineIcon />, path: '/calendario' },
  { text: 'Vista Anual', icon: <CalendarViewMonthIcon />, path: '/annual' },
  { text: 'Trabajadores', icon: <PeopleIcon />, path: '/workers' },
  { text: 'Solicitudes', icon: <EventNoteIcon />, path: '/vacation-requests' },
  { text: 'Centros de Trabajo', icon: <BusinessIcon />, path: '/work-centers' },
  { text: 'Festivos', icon: <EventIcon />, path: '/holidays' },
  { text: 'Bolsas Anuales', icon: <InventoryIcon />, path: '/annual-bags' },
  { text: 'Mi Perfil', icon: <AccountCircleIcon />, path: '/profile' },
];

const Layout: React.FC<Props> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user, isAdmin } = useAuth();
  const { snackbarOpen, snackbarFading, snackbarMessage, closeSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleCollapseToggle = () => {
    // En móvil, cierra el drawer temporal en lugar de colapsar
    if (isMobile) {
      setMobileOpen(false);
    } else {
      setCollapsed(!collapsed);
    }
  };

  const handleMenuClick = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const drawer = (
    <Box sx={{ height: '100%', backgroundColor: '#004254', borderRight: '1px solid #003340' }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: (collapsed && !isMobile) ? 'center' : 'flex-end',
        minHeight: 64,
        px: (collapsed && !isMobile) ? 0 : 2,
        py: 0,
        backgroundColor: '#001923',
        borderBottom: '1px solid #001923',
        position: 'relative'
      }}>
        {!(collapsed && !isMobile) && (
          <Box sx={{ 
            position: 'absolute',
            left: '50%',
            transform: 'translateX(calc(-50% - 20px))', // Compensa el botón de la derecha
            display: 'flex', 
            alignItems: 'center',
            gap: 1.5
          }}>
            <Box
              component="img"
              src={indraLogo}
              alt="Indra Logo"
              sx={{
                height: 32,
                width: 32,
                filter: 'brightness(0) invert(1)',
                display: 'block'
              }}
            />
            <Typography 
              variant="h6" 
              noWrap
              sx={{ 
                fontWeight: 700,
                letterSpacing: '-0.01em',
                color: '#ffffff'
              }}
            >
              VacaFlow
            </Typography>
          </Box>
        )}
        <IconButton 
          onClick={handleCollapseToggle}
          sx={{ 
            color: '#ffffff',
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
          }}
        >
          {/* En móvil siempre muestra ChevronLeft (cerrar), en desktop alterna entre left/right */}
          {isMobile ? <ChevronLeftIcon /> : (collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />)}
        </IconButton>
      </Box>
      <List sx={{ pt: 2, px: (collapsed && !isMobile) ? 1 : 2 }}>
        {menuItems
          .filter(item => {
            // Filter admin-only routes
            const adminRoutes = ['/workers', '/work-centers', '/holidays', '/annual-bags'];
            if (adminRoutes.includes(item.path)) {
              return isAdmin;
            }
            return true;
          })
          .map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => handleMenuClick(item.path)}
              sx={{
                borderRadius: 1,
                py: 1.5,
                justifyContent: (collapsed && !isMobile) ? 'center' : 'flex-start',
                color: '#ffffff',
                '&.Mui-selected': {
                  backgroundColor: '#00B0BD',
                  color: '#001923',
                  '&:hover': {
                    backgroundColor: '#00D4E4',
                  },
                  '& .MuiListItemIcon-root': {
                    color: '#001923',
                  },
                },
                '&:hover': {
                  backgroundColor: 'rgba(0, 176, 189, 0.15)',
                },
              }}
            >
              <ListItemIcon sx={{ 
                minWidth: (collapsed && !isMobile) ? 0 : 36, 
                color: location.pathname === item.path ? 'inherit' : '#ffffff',
                justifyContent: 'center'
              }}>
                {item.icon}
              </ListItemIcon>
              {!(collapsed && !isMobile) && (
                <ListItemText 
                  primary={item.text} 
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                  }}
                />
              )}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${collapsed ? collapsedDrawerWidth : drawerWidth}px)` },
          ml: { sm: `${collapsed ? collapsedDrawerWidth : drawerWidth}px` },
          boxShadow: 'none',
          border: '1px solid #00B0BD',
          borderLeft: 'none',
          backgroundColor: '#00B0BD'
        }}
      >
        <Toolbar sx={{ minHeight: '62px !important' }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' }, color: '#001923' }}
          >
            <MenuIcon />
          </IconButton>
          <Typography 
            variant="h5" 
            noWrap 
            component="div"
            onClick={() => navigate('/')}
            sx={{
              fontWeight: 700,
              letterSpacing: '-0.01em',
              color: '#001923',
              flexGrow: 1,
              fontSize: { xs: '1rem', sm: '1.25rem' },
              cursor: 'pointer',
              '@media (hover: hover)': { '&:hover': { opacity: 0.75 } },
              '&:active': { opacity: 0.75 },
            }}
          >
            {isMobile ? 'VacaFlow' : 'Gestión de Vacaciones'}
          </Typography>
          
          {/* User info and logout */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{ textAlign: 'right', display: 'block', cursor: 'pointer' }}
              onClick={() => navigate('/profile')}
            >
              <Typography variant="body2" sx={{ color: '#001923', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                {user?.name} {user?.surname}
              </Typography>
              <Typography variant="caption" sx={{ color: '#004254', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                {isAdmin ? 'Administrador' : 'Usuario'}
              </Typography>
            </Box>
            <IconButton
              onClick={() => navigate('/profile')}
              sx={{
                color: '#001923',
                display: { xs: 'none', sm: 'flex' },
                '&:hover': { backgroundColor: 'rgba(0, 25, 35, 0.1)' },
              }}
              size="small"
            >
              <AccountCircleIcon />
            </IconButton>
            <Button
              startIcon={<LogoutIcon />}
              onClick={logout}
              sx={{
                color: '#001923',
                borderColor: '#001923',
                '&:hover': {
                  backgroundColor: 'rgba(0, 25, 35, 0.1)',
                  borderColor: '#001923',
                },
                minWidth: { xs: 'auto', sm: 'inherit' },
                px: { xs: 1, sm: 2 },
              }}
              variant="outlined"
              size="small"
            >
              {isMobile ? '' : 'Salir'}
            </Button>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: collapsed ? collapsedDrawerWidth : drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              border: 'none',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: collapsed ? collapsedDrawerWidth : drawerWidth,
              border: 'none',
              transition: 'width 0.2s ease',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{ 
          flexGrow: 1, 
          p: { xs: 2, sm: 4 }, 
          width: { sm: `calc(100% - ${collapsed ? collapsedDrawerWidth : drawerWidth}px)` },
          backgroundColor: '#FAFAFA',
          minHeight: '100vh',
          transition: 'width 0.2s ease',
          color: '#001923',
          overflow: 'hidden',
          '& .MuiTypography-root': {
            color: '#001923'
          },
          '& .descripcion-cyan': {
            color: '#00B0BD !important'
          },
          '& .MuiDataGrid-root': {
            color: '#001923'
          }
        }}
      >
        <Toolbar />
        <Box sx={{ width: '100%', mt: { xs: '10px', sm: 0 } }}>
          {children}
        </Box>
      </Box>

      {/* Toast global — centrado respecto al área de contenido */}
      {snackbarOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: { xs: '70px', sm: '80px' },
            left: {
              xs: 0,
              sm: `${collapsed ? collapsedDrawerWidth : drawerWidth}px`,
            },
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            zIndex: 1400,
            pointerEvents: 'none',
            opacity: snackbarFading ? 0 : 1,
            transition: 'opacity 0.5s ease',
          }}
        >
          <Alert
            onClose={closeSnackbar}
            severity="success"
            sx={{
              borderRadius: 0,
              fontWeight: 400,
              fontSize: '0.95rem',
              px: 4,
              py: 1.5,
              pointerEvents: 'auto',
              boxShadow: 3,
            }}
          >
            {snackbarMessage}
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default Layout;